"use client";

import { motion } from "framer-motion";
import { PrincipleCard } from "@/components/ui/principle-card";
import { cn, containerClasses } from "@/lib/utils";

interface Principle {
  title: string;
  description: string;
  variant: "purple" | "default";
}

interface CorePrinciplesContent {
  title: string;
  principles: Principle[];
}

const staggerContainer = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.3
    }
  }
};

export function CorePrinciples() {
  const content: CorePrinciplesContent = {
    title: "Unsere Prinzipien",
    principles: [
      { title: "Menschlich durch KI", description: "KI als Enabler für echte, wertschätzende Beziehungen", variant: "purple" },
      { title: "Transparenz und Fairness", description: "Klare Prozesse und Kriterien für alle Beteiligten", variant: "default" },
      { title: "Nachhaltiger Mehrwert", description: "Aus einmaligen Kontakten werden langfristige Partnerschaften", variant: "purple" },
      { title: "Messbare Resultate", description: "Konkrete Verbesserungen in Effizienz, Qualität und Zufriedenheit", variant: "default" },
      { title: "Kontinuierliche Innovation", description: "Wir entwickeln unsere Technologie stetig weiter – für noch bessere Ergebnisse", variant: "default" }
    ]
  };

  const { title, principles } = content;

  return (
    <section className={cn(
      "py-24 bg-surface font-gilroy"
    )}>
      <div className={containerClasses}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <h2 className="text-h2 mb-2">
            {title}
          </h2>
          <div className={cn(
            "w-24 h-1 mx-auto rounded-full",
            "bg-thistle"
          )} />
        </motion.div>

        {/* Top Row - 3 Cards */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className={cn(
            "grid grid-cols-1 md:grid-cols-3 gap-6 mb-6",
            "max-w-7xl mx-auto"
          )}
        >
          {principles.slice(0, 3).map((principle, index) => (
            <PrincipleCard
              key={index}
              title={principle.title}
              description={principle.description}
              variant={principle.variant}
            />
          ))}
        </motion.div>

        {/* Bottom Row - 2 Cards */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className={cn(
            "grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12",
            "px-0 md:px-32 lg:px-40 xl:px-52",
            "max-w-7xl mx-auto"
          )}
        >
          {principles.slice(3).map((principle, index) => (
            <PrincipleCard
              key={index + 3}
              title={principle.title}
              description={principle.description}
              variant={principle.variant}
            />
          ))}
        </motion.div>
      </div>
    </section>
  );
} 